import * as THREE from 'three';
import { FikoObje3D } from './FikoObje3D';
import { FikoSahne } from './FikoSahne';

export class FikoGrup extends FikoObje3D {
    override type = 'FikoGrup';
    readonly isGroup: true;

    public get liste(): FikoGrup[] & any {
        let gorunurVar = false;
        for (let index = 0; index < this.children.length; index++) {
            const element = this.children[index];
            if (element.userData['FikoArayuz']?.agactaGorunur) {
                gorunurVar = true
            }
        }
        return gorunurVar ? this.children as FikoGrup[] : [];
    }

    constructor(public override sahne?: FikoSahne, isim?: string) {
        super(sahne);
        this.name = isim;

        // Ağaçta görünür olması için ayarla
        this.userData['FikoArayuz'].agactaGorunur = true;
        this.userData['FikoArayuz'].simge = "./simgeler/grup.svg";
    }

    override ekle?(...objeler: THREE.Object3D[]): FikoGrup {
        super.add(...objeler);

        return this;
    }

    // ─── Montaj Exploded View (parçaları merkezden dışa dağıt) ───
    private _patlamaFaktor = 0;
    private _patlamaAnim: number | null = null;

    public get patlamaFaktoru(): number { return this._patlamaFaktor; }

    /** Parçaları merkezden dışa dağıtır. faktor 0=toplu, 1=tam patlama. Tween'li. */
    public patlat(faktor: number = 1, sure: number = 400): void {
        const parcalar = this.children.filter(c => (c as any).type?.startsWith?.('Fiko'));
        if (parcalar.length < 2) return;

        // Grup-yerel uzayda parça merkezleri ve grup merkezi
        const kutu = new THREE.Box3();
        const merkezL = new THREE.Vector3();
        const merkezler: THREE.Vector3[] = [];
        const grupMerkez = new THREE.Vector3();
        for (const c of parcalar) {
            kutu.setFromObject(c);
            kutu.getCenter(merkezL);
            this.worldToLocal(merkezL); // dünya → grup-yerel
            merkezler.push(merkezL.clone());
            grupMerkez.add(merkezL);
        }
        grupMerkez.divideScalar(parcalar.length);

        // Grup yarıçapı (mesafe ölçeği)
        const tumKutu = new THREE.Box3().setFromObject(this);
        const boyut = new THREE.Vector3(); tumKutu.getSize(boyut);
        const yaricap = Math.max(boyut.length() * 0.5, 1);

        // Her parça için hedef yerel pozisyon hesapla
        const baslangic: THREE.Vector3[] = [];
        const hedef: THREE.Vector3[] = [];
        const yon = new THREE.Vector3();
        for (let i = 0; i < parcalar.length; i++) {
            const c: any = parcalar[i];
            if (!c.userData['_patlamaPos']) c.userData['_patlamaPos'] = c.position.clone();
            const orijinal: THREE.Vector3 = c.userData['_patlamaPos'];

            yon.subVectors(merkezler[i], grupMerkez);
            if (yon.lengthSq() < 1e-6) {
                const a = (i / parcalar.length) * Math.PI * 2;
                yon.set(Math.cos(a), Math.sin(a), 0);
            }
            yon.normalize();

            baslangic.push(c.position.clone());
            hedef.push(orijinal.clone().addScaledVector(yon, yaricap * faktor));
        }

        this._patlamaFaktor = faktor;
        if (this._patlamaAnim !== null) cancelAnimationFrame(this._patlamaAnim);

        const ciz = () => this.sahne?.anaGosterim?.ciz?.();
        if (sure <= 0) {
            for (let i = 0; i < parcalar.length; i++) parcalar[i].position.copy(hedef[i]);
            ciz();
            return;
        }

        const t0 = performance.now();
        const adim = () => {
            const t = Math.min(1, (performance.now() - t0) / sure);
            const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // easeInOutQuad
            for (let i = 0; i < parcalar.length; i++) parcalar[i].position.lerpVectors(baslangic[i], hedef[i], e);
            ciz();
            if (t < 1) this._patlamaAnim = requestAnimationFrame(adim);
            else this._patlamaAnim = null;
        };
        this._patlamaAnim = requestAnimationFrame(adim);
    }

    /** Patlamayı kapatır, parçaları orijinal konuma toplar. */
    public patlamayiTopla(sure: number = 400): void {
        this.patlat(0, sure);
    }

    sahipBulTip(tip: string): any {
        let _rslt = null;
        var aktifObje = this.parent;
        while (aktifObje.parent && _rslt == null) {
            if (aktifObje.type != tip)
                aktifObje = aktifObje.parent
            else {
                _rslt = aktifObje
                break;
            }
        }

        return _rslt;
    }

    override toJSON(meta?: { geometries: any; materials: any; textures: any; images: any }): any {
        const _rslt: any = { type: this.type, isim: this.name, uuid: this.uuid };

        const childs: any[] = [];
        for (let i = 0; i < this.children.length; i++) {
            const element: any = this.children[i];
            if (element.type && element.type.startsWith('Fiko')) {
                childs.push(element.toJSON());
            }
        }
        _rslt.children = childs;

        _rslt.userData = this._serializeUserData();
        _rslt.visible = this.visible;
        _rslt.matrix = this.matrix.toArray();

        return _rslt;
    }

    static fromJson(sahne?: FikoSahne, parent?: any, json?: any, binBuffers?: Uint8Array[]) {

        const _rslt = new FikoGrup(sahne, json.isim);
        _rslt.uuid = json.uuid;
        _rslt.parent = parent;

        if (json.children) {
            const { jsondan } = require('../FikoThreeType');
            for (let i = 0; i < json.children.length; i++) {
                const child = jsondan(sahne, _rslt, json.children[i], binBuffers);
                if (child) _rslt.ekle(child);
            }
        }

        if (json.userData) _rslt._restoreUserData(json.userData);
        if (json.visible !== undefined) _rslt.visible = json.visible;
        if (json.matrix) {
            _rslt.matrix.fromArray(json.matrix);
            _rslt.matrix.decompose(_rslt.position, _rslt.quaternion, _rslt.scale);
        }

        return _rslt;
    }

}
